<template>
  <div
    v-if="visible && current"
    class="waajo-ticket-preview"
    @click.self="close"
  >
    <div
      class="waajo-ticket-preview-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="'Pratinjau ' + displayName"
    >
      <div class="waajo-ticket-preview-head">
        <div class="waajo-ticket-preview-title">
          <strong :title="displayName">{{ displayName }}</strong>
          <small>
            {{ position }} / {{ total }}
            <template v-if="sizeText"> · {{ sizeText }}</template>
          </small>
        </div>
        <div class="waajo-ticket-preview-actions">
          <button
            type="button"
            class="waajo-ticket-preview-btn"
            title="Unduh gambar"
            @click="handleDownload"
          >
            <i class="fa fa-download"></i>
            <span class="waajo-ticket-preview-btn-label">Unduh</span>
          </button>
          <button
            type="button"
            class="waajo-ticket-preview-btn is-icon"
            title="Tutup pratinjau"
            @click="close"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <div class="waajo-ticket-preview-stage">
        <button
          v-if="total > 1"
          type="button"
          class="waajo-ticket-preview-nav is-prev"
          title="Gambar sebelumnya"
          aria-label="Gambar sebelumnya"
          @click="prev"
        >
          <i class="fa fa-angle-left"></i>
        </button>

        <img
          v-if="current.url && !current.broken"
          :key="'preview-' + current.key + '-' + current.url"
          class="waajo-ticket-preview-image"
          :src="current.url"
          :alt="displayName"
          @error="handleImageError"
        />

        <div v-else class="waajo-ticket-preview-state">
          <i class="fa fa-exclamation-circle"></i>
          <p>
            Pratinjau gambar tidak dapat dimuat. Tautan gambar mungkin sudah
            kedaluwarsa.
          </p>
          <button type="button" class="waajo-ticket-preview-retry" @click="$emit('refresh')">
            <i class="fa fa-refresh"></i> Muat ulang gambar
          </button>
        </div>

        <button
          v-if="total > 1"
          type="button"
          class="waajo-ticket-preview-nav is-next"
          title="Gambar berikutnya"
          aria-label="Gambar berikutnya"
          @click="next"
        >
          <i class="fa fa-angle-right"></i>
        </button>
      </div>

      <div v-if="total > 1" class="waajo-ticket-preview-foot">
        <div class="waajo-ticket-preview-dots">
          <button
            v-for="(item, dotIndex) in items"
            :key="'dot-' + item.key"
            type="button"
            class="waajo-ticket-preview-dot"
            :class="{ 'is-active': dotIndex === safeIndex }"
            :title="'Gambar ' + (dotIndex + 1)"
            :aria-label="'Gambar ' + (dotIndex + 1)"
            @click="goTo(dotIndex)"
          ></button>
        </div>
        <small>{{ position }} dari {{ total }} gambar</small>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTicketFileSize } from '@/utils/ticketAttachment'

export default {
  name: 'TicketAttachmentPreview',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    startIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      index: 0,
      previousBodyOverflow: '',
      scrollLocked: false
    }
  },
  computed: {
    total() {
      return Array.isArray(this.items) ? this.items.length : 0
    },
    safeIndex() {
      if (!this.total) return 0
      return Math.min(Math.max(0, this.index), this.total - 1)
    },
    current() {
      if (!this.total) return null
      return this.items[this.safeIndex] || null
    },
    position() {
      return this.total ? this.safeIndex + 1 : 0
    },
    displayName() {
      return (this.current && this.current.name) || 'Lampiran gambar'
    },
    sizeText() {
      return this.current ? formatTicketFileSize(this.current.size) : ''
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.open()
      } else {
        this.release()
      }
    },
    startIndex(value) {
      if (!this.visible) return
      this.index = this.clampIndex(value)
    },
    items() {
      if (!this.visible) return
      if (!this.total) {
        this.close()
        return
      }
      this.index = this.clampIndex(this.index)
    }
  },
  mounted() {
    if (this.visible) this.open()
  },
  beforeDestroy() {
    this.release()
  },
  methods: {
    clampIndex(value) {
      if (!this.total) return 0
      const index = Math.floor(Number(value) || 0)
      return Math.min(Math.max(0, index), this.total - 1)
    },
    open() {
      this.index = this.clampIndex(this.startIndex)
      this.lockScroll()
      this.bindKeyboard()
    },
    release() {
      this.unlockScroll()
      this.unbindKeyboard()
    },
    lockScroll() {
      if (typeof document === 'undefined' || !document.body) return
      if (this.scrollLocked) return
      this.previousBodyOverflow = document.body.style.overflow || ''
      document.body.style.overflow = 'hidden'
      this.scrollLocked = true
    },
    unlockScroll() {
      if (typeof document === 'undefined' || !document.body) return
      if (!this.scrollLocked) return
      document.body.style.overflow = this.previousBodyOverflow || ''
      this.previousBodyOverflow = ''
      this.scrollLocked = false
    },
    bindKeyboard() {
      if (typeof document === 'undefined') return
      this.unbindKeyboard()
      document.addEventListener('keydown', this.handleKeydown)
    },
    unbindKeyboard() {
      if (typeof document === 'undefined') return
      document.removeEventListener('keydown', this.handleKeydown)
    },
    handleKeydown(event) {
      if (!this.visible || !event) return
      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.prev()
        return
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.next()
      }
    },
    close() {
      this.release()
      this.$emit('close')
    },
    goTo(index) {
      this.index = this.clampIndex(index)
    },
    prev() {
      if (this.total <= 1) return
      this.index = this.safeIndex <= 0 ? this.total - 1 : this.safeIndex - 1
    },
    next() {
      if (this.total <= 1) return
      this.index = this.safeIndex >= this.total - 1 ? 0 : this.safeIndex + 1
    },
    handleImageError() {
      if (this.current) this.$emit('image-error', this.current)
    },
    handleDownload() {
      if (!this.current) return
      this.$emit('download', this.current, this.safeIndex)
    }
  }
}
</script>

<style scoped>
.waajo-ticket-preview {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.78);
}
.waajo-ticket-preview-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 960px;
  max-height: calc(100vh - 36px);
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.36);
}
.waajo-ticket-preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--waajo-border);
}
.waajo-ticket-preview-title {
  min-width: 0;
}
.waajo-ticket-preview-title strong {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: var(--waajo-text);
}
.waajo-ticket-preview-title small {
  display: block;
  margin-top: 3px;
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-preview-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}
.waajo-ticket-preview-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9999px;
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}
.waajo-ticket-preview-btn.is-icon {
  width: 34px;
  padding: 0;
  justify-content: center;
  border-color: #e2e8f0;
  background: #fff;
  color: var(--waajo-text-muted);
  font-size: 15px;
}
.waajo-ticket-preview-btn:disabled {
  opacity: 0.6;
}
.waajo-ticket-preview-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 220px;
  padding: 14px;
  overflow: auto;
  background: #0f172a;
}
.waajo-ticket-preview-image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 210px);
  object-fit: contain;
  border-radius: 8px;
  background: #0f172a;
}
.waajo-ticket-preview-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 30px 20px;
  text-align: center;
  color: #e2e8f0;
  font-size: 13px;
  line-height: 1.5;
}
.waajo-ticket-preview-state i {
  font-size: 30px;
  color: #f7c873;
}
.waajo-ticket-preview-state p {
  margin: 0;
  max-width: 420px;
}
.waajo-ticket-preview-retry {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--waajo-brand);
  border-radius: 9999px;
  background: var(--waajo-brand);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}
.waajo-ticket-preview-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.6);
  color: #fff;
  font-size: 22px;
}
.waajo-ticket-preview-nav:hover,
.waajo-ticket-preview-nav:focus {
  background: rgba(15, 23, 42, 0.86);
}
.waajo-ticket-preview-nav.is-prev {
  left: 12px;
}
.waajo-ticket-preview-nav.is-next {
  right: 12px;
}
.waajo-ticket-preview-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid var(--waajo-border);
  background: var(--waajo-brand-soft-2);
}
.waajo-ticket-preview-foot small {
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-ticket-preview-dots {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.waajo-ticket-preview-dot {
  width: 9px;
  height: 9px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f6d49b;
}
.waajo-ticket-preview-dot.is-active {
  width: 20px;
  border-radius: 9999px;
  background: var(--waajo-brand);
}
@media (max-width: 640px) {
  .waajo-ticket-preview {
    padding: 8px;
  }
  .waajo-ticket-preview-dialog {
    max-height: calc(100vh - 16px);
    border-radius: 12px;
  }
  .waajo-ticket-preview-btn-label {
    display: none;
  }
  .waajo-ticket-preview-btn {
    width: 34px;
    padding: 0;
    justify-content: center;
  }
  .waajo-ticket-preview-nav {
    width: 36px;
    height: 36px;
    font-size: 18px;
  }
  .waajo-ticket-preview-image {
    max-height: calc(100vh - 180px);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketAttachmentPreview.vue